<template>
  <div
    class="pay-range-subscription-card"
    :class="[
      `pay-range-subscription-card--${cardState}`,
      { 'pay-range-subscription-card--with-action': showUnsubscribe },
    ]"
  >
    <div class="pay-range-subscription-card__body">
      <div class="pay-range-subscription-card__identity">
        <div class="pay-range-subscription-card__title-row">
          <img
            v-if="flagSrc"
            class="pay-range-subscription-card__flag"
            :src="flagSrc"
            alt=""
            width="16"
            height="16"
          />
          <button
            type="button"
            class="pay-range-subscription-card__title"
            @click="onTitleClick"
          >
            {{ titleText }}
          </button>
          <PayPlanLifecycleBadge
            v-if="statusBadge"
            class="pay-range-subscription-card__status"
            :status="statusBadge.tone"
            :label="statusBadge.label"
          />
          <PayPlanPeriodStateBadge
            v-if="showStateBadge"
            class="pay-range-subscription-card__state"
            :state="periodState"
            filled
          />
        </div>
        <p v-if="subtitleText" class="pay-range-subscription-card__subtitle">{{ subtitleText }}</p>
        <p v-if="metaLine" class="pay-range-subscription-card__meta">{{ metaLine }}</p>
      </div>

      <div class="pay-range-subscription-card__readiness">
        <div class="pay-range-subscription-card__amounts">
          <button
            v-if="payRangeId != null && payRangeId !== ''"
            type="button"
            class="pay-range-subscription-card__open"
            aria-label="Open pay range"
            @click.stop="onTitleClick"
          >
            <img
              class="pay-range-subscription-card__open-icon"
              :src="linkExternalIcon"
              alt=""
              width="16"
              height="16"
            />
          </button>
          <PayRangePenetrationBar
            v-if="!readinessEnabled"
            class="pay-range-subscription-card__penetration"
            :range-penetration="rangeBounds"
            :currency-symbol="currencySymbol"
            :show-label="false"
          />
          <EXTooltip v-else placement="top" :delay="300" :title="READINESS_TOOLTIP">
            <PayRangeReadinessAmounts
              size="sm"
              :min="formattedAmounts.min"
              :at50="formattedAmounts.at50"
              :at100="formattedAmounts.at100"
              :max="formattedAmounts.max"
            />
          </EXTooltip>
        </div>
        <div
          v-if="versionLine || showPreviousVersionChevron || showNextVersionChevron"
          class="pay-range-subscription-card__version-row"
        >
          <button
            v-if="showPreviousVersionChevron"
            type="button"
            class="pay-range-subscription-card__version-chevron pay-range-subscription-card__version-chevron--previous"
            aria-label="Previous version"
            @click="onPreviousVersion"
          >
            <img
              class="pay-range-subscription-card__version-chevron-icon"
              :src="chevronLeftIcon"
              alt=""
              width="16"
              height="16"
            />
          </button>
          <p v-if="versionLine" class="pay-range-subscription-card__version">{{ versionLine }}</p>
          <button
            v-if="showNextVersionChevron"
            type="button"
            class="pay-range-subscription-card__version-chevron pay-range-subscription-card__version-chevron--next"
            aria-label="Next version"
            @click="onNextVersion"
          >
            <img
              class="pay-range-subscription-card__version-chevron-icon"
              :src="chevronRightIcon"
              alt=""
              width="16"
              height="16"
            />
          </button>
        </div>
      </div>
    </div>

    <div v-if="showUnsubscribe" class="pay-range-subscription-card__action">
      <MenuIcon
        v-if="moreActionsEnabled"
        :dropdownItems="menuItems"
        :width="200"
        @onClick="onMenuAction"
      >
        <template #activator>
          <button
            type="button"
            class="pay-range-subscription-card__more"
            aria-label="More actions"
            aria-haspopup="menu"
          >
            <img
              class="pay-range-subscription-card__more-icon"
              :src="dotsVerticalIcon"
              alt=""
              width="20"
              height="20"
            />
          </button>
        </template>
      </MenuIcon>
      <button
        v-else
        type="button"
        class="pay-range-subscription-card__unsubscribe"
        aria-label="Unsubscribe"
        @mouseenter="openTooltip"
        @mouseleave="closeTooltip"
        @focus="openTooltip"
        @blur="closeTooltip"
        @click.stop="onUnsubscribe"
      >
        <img
          class="pay-range-subscription-card__unsubscribe-icon"
          :src="xCloseIcon"
          alt=""
          width="20"
          height="20"
        />
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount } from "vue";
import PayPlanLifecycleBadge from "@components/PayPlanLifecycleBadge/PayPlanLifecycleBadge.vue";
import PayPlanPeriodStateBadge from "@components/PayPlanPeriodStateBadge/PayPlanPeriodStateBadge.vue";
import PayRangePenetrationBar from "@components/PayRangePenetrationBar/PayRangePenetrationBar.vue";
import PayRangeReadinessAmounts from "@components/PayRangeReadinessAmounts/PayRangeReadinessAmounts.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import MenuIcon from "@components/MenuIcon/MenuIcon.vue";
import { getCurrencySymbol } from "@components/FairPayStatusShort/formatFairPayStatusAmount";
import { versionStatusBadge } from "@components/FairPay/payRangeBadges";
import { useEffectiveDatedFlag } from "@components/utils/useEffectiveDatedFlag";
import { useFairPayMoreActionsFlag } from "@components/utils/useFairPayMoreActionsFlag";
import xCloseIcon from "@components/assets/x-close.svg";
import dotsVerticalIcon from "@components/assets/dots-vertical.svg";
import linkExternalIcon from "@components/assets/link-external-01.svg";
import chevronLeftIcon from "./icon-chevron-left.svg";
import chevronRightIcon from "./icon-chevron-right.svg";
import {
  deriveSubscriptionState,
  canUnsubscribe,
  subscriptionSubtitle,
  subscriptionPeriodState,
  versionWindowLine,
  versionPreviousTargetDate,
  versionNextTargetDate,
  formatCardAmount,
} from "./payRangeSubscriptionCardUtils";

const READINESS_TOOLTIP = "At 0%, 50%, 100% and 150% readiness";

const props = defineProps({
  /** Rule A-40: false replaces the readiness anchors with the Min / Mid / Max range. */
  readinessEnabled: {
    type: Boolean,
    default: true,
  },
  /** Status of the subscribed version as the API sends it (Draft / In Force / Retired / Voided). */
  status: {
    type: String,
    default: "",
  },
  subscriptionId: {
    type: [String, Number],
    default: null,
  },
  payRangeId: {
    type: [String, Number],
    default: null,
  },
  payRangeCode: {
    type: String,
    default: "",
  },
  payRangeName: {
    type: String,
    default: "",
  },
  countryFlag: {
    type: String,
    default: "",
  },
  countryCode: {
    type: String,
    default: "",
  },
  currencyCode: {
    type: String,
    default: "",
  },
  payFrequency: {
    type: String,
    default: "",
  },
  locationName: {
    type: String,
    default: "",
  },
  subscriptionEffectiveFrom: {
    type: [String, Date],
    default: null,
  },
  subscriptionEffectiveTo: {
    type: [String, Date],
    default: null,
  },
  versionEffectiveFrom: {
    type: [String, Date],
    default: null,
  },
  versionEffectiveTo: {
    type: [String, Date],
    default: null,
  },
  versionNumber: {
    type: [String, Number],
    default: null,
  },
  previousVersionEffectiveTo: {
    type: [String, Date],
    default: null,
  },
  nextVersionEffectiveFrom: {
    type: [String, Date],
    default: null,
  },
  amountMin: {
    type: [String, Number],
    default: null,
  },
  amountAt50: {
    type: [String, Number],
    default: null,
  },
  amountAt100: {
    type: [String, Number],
    default: null,
  },
  amountMax: {
    type: [String, Number],
    default: null,
  },
  today: {
    type: [String, Date],
    default: null,
  },
});

const emit = defineEmits([
  "unsubscribe",
  "open-pay-range",
  "navigate-to-previous-version",
  "navigate-to-next-version",
]);

const cardState = computed(() =>
  deriveSubscriptionState(
    props.subscriptionEffectiveFrom,
    props.subscriptionEffectiveTo,
    props.today || undefined
  )
);

const showUnsubscribe = computed(() => canUnsubscribe(cardState.value));

const moreActionsEnabled = useFairPayMoreActionsFlag();
const menuItems = [
  { id: "open", name: "Open pay range" },
  { id: "unsubscribe", name: "Unsubscribe\u2026", destructive: true },
];

const effectiveDatedEnabled = useEffectiveDatedFlag();
const periodState = computed(() => subscriptionPeriodState(cardState.value));
const statusBadge = computed(() =>
  effectiveDatedEnabled.value ? versionStatusBadge(props.status) : null
);
const showStateBadge = computed(() => effectiveDatedEnabled.value && !!periodState.value);

const titleText = computed(() => {
  const code = String(props.payRangeCode || "").trim();
  const name = String(props.payRangeName || "").trim();
  if (code && name) return `${code} · ${name}`;
  return name || code;
});

const flagSrc = computed(() => {
  if (props.countryFlag) return props.countryFlag;
  const code = String(props.countryCode || "").trim().toLowerCase();
  return code ? `https://flagcdn.com/${code}.svg` : "";
});

const metaLine = computed(() =>
  [props.currencyCode, props.payFrequency, props.locationName]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" · ")
);

const subtitleText = computed(() =>
  subscriptionSubtitle(
    cardState.value,
    props.subscriptionEffectiveFrom,
    props.subscriptionEffectiveTo
  )
);

const currencySymbol = computed(() => getCurrencySymbol(props.currencyCode || "USD"));

// Range bounds only: the position list has no amount to place, so there is no position_pct.
const rangeBounds = computed(() => {
  const min = props.amountMin;
  const max = props.amountMax;
  const bothPresent = [min, max].every((v) => v != null && v !== "" && Number.isFinite(Number(v)));
  return {
    min,
    mid: bothPresent ? (Number(min) + Number(max)) / 2 : null,
    max,
    position_pct: null,
  };
});

const formattedAmounts = computed(() => ({
  min: formatCardAmount(props.amountMin),
  at50: formatCardAmount(props.amountAt50),
  at100: formatCardAmount(props.amountAt100),
  max: formatCardAmount(props.amountMax),
}));

const versionLine = computed(() =>
  versionWindowLine(
    props.versionEffectiveFrom,
    props.versionEffectiveTo,
    props.subscriptionEffectiveTo,
    props.versionNumber
  )
);

const showPreviousVersionChevron = computed(() =>
  versionPreviousTargetDate(props.previousVersionEffectiveTo, props.subscriptionEffectiveFrom)
    ? true
    : false
);

const showNextVersionChevron = computed(() =>
  versionNextTargetDate(props.nextVersionEffectiveFrom, props.subscriptionEffectiveTo)
    ? true
    : false
);

function onPreviousVersion() {
  const targetDate = versionPreviousTargetDate(
    props.previousVersionEffectiveTo,
    props.subscriptionEffectiveFrom
  );
  if (targetDate == null) return;
  emit("navigate-to-previous-version", { targetDate });
}

function onNextVersion() {
  const targetDate = versionNextTargetDate(
    props.nextVersionEffectiveFrom,
    props.subscriptionEffectiveTo
  );
  if (targetDate == null) return;
  emit("navigate-to-next-version", { targetDate });
}

let tooltipEl = null;

function ensureTooltip() {
  if (tooltipEl) return tooltipEl;
  tooltipEl = document.createElement("div");
  tooltipEl.className = "pay-range-subscription-card-tooltip";
  tooltipEl.setAttribute("role", "tooltip");
  tooltipEl.textContent = "Unsubscribe";
  document.body.appendChild(tooltipEl);
  return tooltipEl;
}

function openTooltip(event) {
  const target = event && event.currentTarget;
  if (!target || typeof target.getBoundingClientRect !== "function") return;
  const rect = target.getBoundingClientRect();
  const el = ensureTooltip();
  el.style.display = "block";
  el.style.position = "fixed";
  el.style.zIndex = "9999";
  el.style.left = `${rect.left + rect.width / 2}px`;
  el.style.top = `${rect.top}px`;
  el.style.transform = "translate(-50%, calc(-100% - 4px))";
}

function closeTooltip() {
  if (tooltipEl) tooltipEl.style.display = "none";
}

onBeforeUnmount(() => {
  if (tooltipEl?.parentNode) {
    tooltipEl.parentNode.removeChild(tooltipEl);
    tooltipEl = null;
  }
});

function onTitleClick() {
  if (props.payRangeId == null || props.payRangeId === "") return;
  emit("open-pay-range", props.payRangeId);
}

function onUnsubscribe() {
  closeTooltip();
  emit("unsubscribe", props.subscriptionId);
}

function onMenuAction(item) {
  if (item?.id === "open") onTitleClick();
  else if (item?.id === "unsubscribe") onUnsubscribe();
}
</script>

<style scoped>
.pay-range-subscription-card {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 16px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  background: #fff;
  box-sizing: border-box;
}

.pay-range-subscription-card--lapsed {
  background: #f9fafb;
}

.pay-range-subscription-card--current-closed,
.pay-range-subscription-card--current-open {
  background: #fff;
}

.pay-range-subscription-card--future-closed,
.pay-range-subscription-card--future-open {
  background: #f0f9ff;
}

.pay-range-subscription-card--with-action {
  gap: 24px;
  padding: 16px 5px 16px 16px;
}

.pay-range-subscription-card__body {
  display: flex;
  flex: 1 1 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  min-width: 0;
}

.pay-range-subscription-card__identity {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  min-width: 0;
}

.pay-range-subscription-card__title-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.pay-range-subscription-card__flag {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
}

.pay-range-subscription-card__title {
  flex: 0 1 auto;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 700;
  font-style: normal;
  line-height: normal;
  color: #101828;
  white-space: normal;
  overflow-wrap: anywhere;
  cursor: pointer;
}

.pay-range-subscription-card__open {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: 3px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.pay-range-subscription-card__open-icon {
  display: block;
  width: 16px;
  height: 16px;
}

.pay-range-subscription-card__subtitle,
.pay-range-subscription-card__version,
.pay-range-subscription-card__meta {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  font-style: normal;
  line-height: 18px;
  color: #667085;
}

.pay-range-subscription-card__version-row {
  display: flex;
  align-items: center;
  gap: 2px;
}

.pay-range-subscription-card__version-chevron {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 2px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  box-sizing: border-box;
}

.pay-range-subscription-card__version-chevron:hover {
  background: #f2f4f7;
}

.pay-range-subscription-card__version-chevron-icon {
  display: block;
  width: 16px;
  height: 16px;
}

.pay-range-subscription-card__meta {
  white-space: nowrap;
}

.pay-range-subscription-card__readiness {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  justify-content: center;
  flex: 0 0 auto;
  white-space: nowrap;
}

.pay-range-subscription-card__amounts {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  font-style: normal;
  line-height: 18px;
}

.pay-range-subscription-card__action {
  flex-shrink: 0;
}

.pay-range-subscription-card__unsubscribe {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  box-sizing: border-box;
}

.pay-range-subscription-card__more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  box-sizing: border-box;
}

.pay-range-subscription-card__more:hover {
  background: #f2edff;
}

.pay-range-subscription-card__more:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.pay-range-subscription-card__more-icon {
  display: block;
  width: 20px;
  height: 20px;
}

.pay-range-subscription-card__unsubscribe-icon {
  display: block;
  width: 20px;
  height: 20px;
}
</style>

<style>
.pay-range-subscription-card-tooltip {
  position: fixed;
  transform: translate(-50%, calc(-100% - 4px));
  padding: 8px 12px;
  border-radius: 8px;
  background: #0c111d;
  color: #fff;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  font-style: normal;
  line-height: 18px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 4000;
}

.pay-range-subscription-card-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #0c111d;
}
</style>
